import {
  CreateUIAnalyticsEvent,
  UIAnalyticsEvent
} from '@atlaskit/analytics-next';
import {
  ProductKeys,
  BitbucketWorkspace,
  Sites,
  ProductPayloadObject
} from '../../model/wac-signup-form';
import { NewRelicTracking, SegmentType } from '@atlassiansox/bxpkit-core';
import { uniq } from 'lodash';
import { DeferSitenameCohort } from '../../layouts/legacy/view/utility/get-defer-sitename-experiment-cohort';
import { Environment } from '@atlassiansox/bxpkit-core';
import {
  MarieSchemaAnalyticsType,
  Product
} from '@atlassiansox/bxpkit-core/src';

type CreateSolutionStatsEventType = {
  readonly workspaceUuid?: string;
  readonly signUpFormUIState: { [key: string]: any };
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
};

/**
 * This function returns an analytics-next event for
 * tracking which products were provisioned vs existing,
 * as well as the primary cloudId & any existing instance IDs
 * in the cloud provisioning (or activateProducts) request.
 *
 * @param signUpFormUIState - complete signup form UIState object
 * @param createAnalyticsEvent - analytics-next function, to be passed in from UI
 */

export function createSolutionStatsEvent({
  signUpFormUIState,
  createAnalyticsEvent
}: CreateSolutionStatsEventType): UIAnalyticsEvent | undefined {
  const {
    signupState,
    isBbCopyEnabled,
    bbWorkspaces,
    productsExisting,
    pickableSites,
    isBundleBBJSW,
    isBundleDevops
  } = signUpFormUIState;

  if (!isBundleDevops && !isBundleBBJSW) {
    return;
  }

  let isNewSiteProvisioned = false;
  const isN2NAtlassian = productsExisting.length === 0;
  const isNewBitbucketWorkspace = isBbCopyEnabled || bbWorkspaces.length === 0;
  const signupUrl = window?.location?.href || '';
  const targetCloudId = signupState?.selectedSite?.cloudId || null;

  const existingProducts = uniq(productsExisting);
  let existingInstanceIds: string[] = [];
  let provisionedProducts: string[] = [];

  // gather all existing instance IDs
  if (bbWorkspaces && bbWorkspaces.length > 0) {
    existingInstanceIds = existingInstanceIds.concat(
      bbWorkspaces.map(
        (workspace: BitbucketWorkspace) => workspace?.workspace?.uuid
      )
    );
  }
  if (pickableSites && pickableSites.length > 0) {
    existingInstanceIds = existingInstanceIds.concat(
      pickableSites.map((site: Sites) => site?.cloudId)
    );
  }

  if (isBundleBBJSW) {
    // either existing sitepicker is not rendered, or a site
    // was selected without JSW included
    isNewSiteProvisioned =
      !signupState.isExistingSitePickerN2E ||
      (signupState?.selectedSite?.products &&
        !signupState?.selectedSite?.products.includes(
          ProductKeys.JIRA_SOFTWARE
        ));

    // potentially provisioned products for BB/JSW
    provisionedProducts = [
      ProductKeys.JIRA_SOFTWARE,
      ProductKeys.BITBUCKET
    ].map((key) => key.toString());

    // filter out provisioned products if existing products were used
    if (!isNewSiteProvisioned) {
      provisionedProducts = provisionedProducts.filter(
        (key) => key != ProductKeys.JIRA_SOFTWARE
      );
    }

    if (!isNewBitbucketWorkspace) {
      provisionedProducts = provisionedProducts.filter(
        (key) => key != ProductKeys.BITBUCKET
      );
    }
  }

  existingInstanceIds = uniq(existingInstanceIds);

  return createAnalyticsEvent({
    eventName: 'bxp.formCloudSignup.solutionStats',
    event: 'clicked',
    eventComponent: 'button',
    data: {
      isN2NAtlassian,
      signupUrl,
      existingProducts,
      provisionedProducts,
      existingInstanceIds,
      isNewSiteProvisioned,
      targetCloudId,
      provisionedCloudId: null
    }
  });
}

export type TrackSignupButtonClickedOptions = {
  context: MarieSchemaAnalyticsType;
  buttonLabel: string | undefined;
  createAnalyticsEvent: CreateUIAnalyticsEvent;
};
export const trackSignupButtonClicked = ({
  context,
  buttonLabel,
  createAnalyticsEvent
}: TrackSignupButtonClickedOptions) => {
  const eventName = 'bxp.formCloudSignup.signup-button-clicked';
  const data = {
    eventType: 'track',
    eventContainer: 'signUpForm',
    event: 'clicked',
    eventComponent: 'button',
    uuid: '6707236536632157',
    label: buttonLabel,
    productKey: context.productKey,
    productsOffered: context.productsOffered,
    loginUserDetected: context.loginUserDetected,
    userEmail: context.userEmail,
    suiteName: context.suiteName,
    productEdition: context.productEdition,
    productPlatform: context.productPlatform,
    schemaVersion: context.schemaVersion
  };

  context?.analyticsTrack?.segment(eventName, data);
  createAnalyticsEvent({ eventName, data }).fire();
};

export type TrackN2ESubmitOptions = {
  cloudId: string | undefined;
  products: Product[] | null;
  segment: SegmentType;
};
export const trackN2ESubmit = ({
  cloudId,
  products,
  segment
}: TrackN2ESubmitOptions) => {
  segment('bxp.formCloudSignup.N2ESubmit', {
    cloudId,
    products,
    signupUrl: window.location.href
  });
};

export type TrackN2ESubmitOptions2 = {
  cloudId: string;
  products: Product[];
  signupUrl: string;
  accountId: string;
  createAnalyticsEvent: CreateUIAnalyticsEvent;
};
export const trackN2ESubmit2 = ({
  cloudId,
  products,
  signupUrl,
  accountId,
  createAnalyticsEvent
}: TrackN2ESubmitOptions2) => {
  createAnalyticsEvent({
    eventName: 'bxp.formCloudSignup.N2ESubmit',
    event: 'clicked',
    eventComponent: 'button',
    targetCloudId: cloudId,
    provisionedProducts: products,
    signupUrl,
    aaid: accountId
  }).fire();
};

export type TrackRecaptchaErrorOptions = {
  segment: SegmentType;
};
export const trackRecaptchaError = ({
  segment
}: TrackRecaptchaErrorOptions) => {
  segment('bxp.formCloudSignup.reCaptchaError', {});
};

export type TrackSubmitWithCaptchaOptions = {
  newRelic: NewRelicTracking;
};
export const trackSubmitWithCaptcha = ({
  newRelic
}: TrackSubmitWithCaptchaOptions) => {
  newRelic.action('bxp.formCloudSignup.submitWithCaptcha', '');
};

export type TrackSignUpFormSubmitSucceededOptions = {
  createAnalyticsEvent: CreateUIAnalyticsEvent;
};
export const trackSignUpFormSubmitSucceeded = ({
  createAnalyticsEvent
}: TrackSignUpFormSubmitSucceededOptions) => {
  createAnalyticsEvent({
    eventName: 'reactSignUpFormSubmitSucceeded',
    data: {
      event: 'submitSucceeded', // event verb, in past tense (eg, "clicked")
      uuid: 'reactSignUpForm' // required for Magnolia events, unique id for a given component if available
    }
  }).fire();
};

export type TrackSignUpFormSubmitFailedOptions = {
  createAnalyticsEvent: CreateUIAnalyticsEvent;
};
export const trackSignUpFormSubmitFailed = ({
  createAnalyticsEvent
}: TrackSignUpFormSubmitFailedOptions) => {
  createAnalyticsEvent({
    eventName: 'reactSignUpFormSubmitFailed',
    data: {
      event: 'submitFailed', // event verb, in past tense (eg, "clicked")
      uuid: 'reactSignUpForm' // required for Magnolia events, unique id for a given component if available
    }
  }).fire();
};

export type TrackSignupContextTooLargeOptions = {
  contextLength: number;
  newRelic: NewRelicTracking;
};
export const trackSignupContextTooLarge = ({
  contextLength,
  newRelic
}: TrackSignupContextTooLargeOptions) => {
  newRelic.action(
    'bxp.formCloudSignup.signupContextTooLarge',
    contextLength.toString()
  );
};

export type TrackActivateProductsSubmitOptions = {
  activateProducts: boolean;
  segment: SegmentType;
};
export const trackActivateProductsSubmit = ({
  activateProducts,
  segment
}: TrackActivateProductsSubmitOptions) => {
  segment('bxp.formCloudSignup.activateProductsSubmit', {
    activateProducts
  });
};

export type TrackFormSubmitOptions = {
  isLoggedIn: boolean | undefined;
  segment: SegmentType;
  newRelic: NewRelicTracking;
};
export const trackFormSubmit = ({
  isLoggedIn,
  segment,
  newRelic
}: TrackFormSubmitOptions) => {
  segment('bxp.formCloudSignup.formSubmit', {
    type: isLoggedIn ? 'N2E' : 'N2N'
  });
  newRelic.action('bxp.formCloudSignup.formSubmit', isLoggedIn ? 'N2E' : 'N2N');
};

export type TrackProgressPollInvalidOptions = {
  segment: SegmentType;
};
export const trackProgressPollInvalid = ({
  segment
}: TrackProgressPollInvalidOptions) => {
  segment('bxp.formCloudSignup.progressPollInvalid', { label: 'cloudName' });
};

export type TrackWaitedSignupPageSubmitMoreThan15S = {
  newRelic: NewRelicTracking;
  segment: SegmentType;
};
export const trackWaitedSignupPageSubmitMoreThan15SOptions = ({
  newRelic,
  segment
}: TrackWaitedSignupPageSubmitMoreThan15S) => {
  const eventName = 'bxp.formCloudSignup.waitedSignupPageSubmit>15s';
  newRelic.action(eventName, '');
  segment(eventName, {});
};

export type TrackWaitedSignupPageSubmitMoreThan60SOptions = {
  newRelic: NewRelicTracking;
  segment: SegmentType;
};
export const trackWaitedSignupPageSubmitMoreThan60S = ({
  newRelic,
  segment
}: TrackWaitedSignupPageSubmitMoreThan60SOptions) => {
  const eventName = 'bxp.formCloudSignup.waitedSignupPageSubmit>60s';
  newRelic.action(eventName, '');
  segment(eventName, {});
};

export type TrackProgressUriErrOptions = {
  err: any;
  newRelic: NewRelicTracking;
  segment: SegmentType;
};
export const trackProgressUriErr = ({
  err,
  newRelic,
  segment
}: TrackProgressUriErrOptions) => {
  const eventName = 'bxp.formCloudSignup.progressUri';
  segment(eventName, { err });
  newRelic.trackAjax(eventName, err, false);
};

export type TrackProgressUriOptions = {
  response: any;
  newRelic: NewRelicTracking;
};
export const trackProgressUri = ({
  response,
  newRelic
}: TrackProgressUriOptions) => {
  newRelic.trackAjax('bxp.formCloudSignup.progressUri', response, true);
};

export type TrackProgressPollMissingOptions = {
  segment: SegmentType;
};
export const trackProgressPollMissing = ({
  segment
}: TrackProgressPollMissingOptions) => {
  segment('bxp.formCloudSignup.progressPollMissing', { label: 'body' });
};

export type TrackSubmitSuccessOptions = {
  isLoggedIn: boolean;
  segment: SegmentType;
};
export const trackSubmitSuccess = ({
  isLoggedIn,
  segment
}: TrackSubmitSuccessOptions) => {
  segment('bxp.formCloudSignup.submitSuccess', {
    type: isLoggedIn ? 'N2E' : 'N2N'
  });
  segment('bxp.formCloudSignup.confirmedEvaluation', {});
};

export type TrackGoToBitbucketOptions = {
  newRelic: NewRelicTracking;
};
export const trackGoToBitbucket = ({ newRelic }: TrackGoToBitbucketOptions) => {
  newRelic.action('bxp.formCloudSignup.goToBitbucket', '');
};

export type TrackGoToJSWOptions = {
  newRelic: NewRelicTracking;
};
export const trackGoToJSW = ({ newRelic }: TrackGoToJSWOptions) => {
  newRelic.action('bxp.formCloudSignup.goToJSW', '');
};

export type TrackProgressPollErrorOptions = {
  label: string;
  segment: SegmentType;
};
export const trackProgressPollError = ({
  label,
  segment
}: TrackProgressPollErrorOptions) => {
  segment('bxp.formCloudSignup.progressPollError', { label });
};

export type TrackProgressPollOptions = {
  label: string;
  segment: SegmentType;
};

export type TrackSubmitSuccessProductsOptions = {
  products: ProductPayloadObject[];
  isLoggedIn: boolean;
  segment: SegmentType;
};
export const trackSubmitSuccessProducts = ({
  isLoggedIn,
  segment
}: TrackSubmitSuccessProductsOptions) => {
  segment('bxp.formCloudSignup.submitSuccess', {
    type: isLoggedIn ? 'N2E' : 'N2N'
  });
};

interface TrackDeferSitenameSignups {
  isSitenameSpecified: boolean;
  isSiteNew: boolean;
  numberOfExistingUserSites: number;
  optionalSitenameFeatureCohort: DeferSitenameCohort;
  sitename: string | null;
  userId: string;
}

// add to atlassian-service-clients if this gets productionized
export const trackDeferSitenameSignups = ({
  isSitenameSpecified,
  isSiteNew,
  optionalSitenameFeatureCohort,
  numberOfExistingUserSites,
  sitename,
  userId
}: TrackDeferSitenameSignups) => {
  fetch(
    `${Environment.getApiPrivateUrl()}/bxp/signup/analytics/deferred-sitename-signup`,
    {
      method: 'POST',
      headers: [['Content-Type', 'application/json']],
      body: JSON.stringify({
        action: 'provisioningInitiated',
        actionSubject: 'product',
        product: 'marketing',
        attributes: {
          isSiteNew,
          isSitenameSpecified,
          numberOfExistingUserSites,
          optionalSitenameFeatureCohort,
          sitename
        },
        userId,
        tenantId: 'tenantId',
        tenantIdType: 'tenantIdType'
      })
    }
  ).catch((err) => console.log(err));
};

export interface TrackExposedToDeferSitename {
  createAnalyticsEvent: CreateUIAnalyticsEvent;
  value: DeferSitenameCohort;
  products: string[];
}

export const trackExposedToDeferSitename = ({
  createAnalyticsEvent,
  value,
  products
}: TrackExposedToDeferSitename) =>
  createAnalyticsEvent({
    action: 'exposed',
    actionSubject: 'feature',
    product: 'marketing',
    attributes: {
      flagKey: 'economy.growth.defer-site-name.experiment',
      value,
      growthInitiative: 'signup',
      products
    }
  }).fire();

export interface TrackErrorParsingAnchorJourneyCookieOptions {
  createAnalyticsEvent: CreateUIAnalyticsEvent;
}

export const trackErrorParsingAnchorJourneyCookie = ({
  createAnalyticsEvent
}: TrackErrorParsingAnchorJourneyCookieOptions) => {
  createAnalyticsEvent({
    eventName: 'bxp.anchorJourney.parseCookieError'
  }).fire();
};
